import { forwardRef, type ButtonHTMLAttributes } from 'react';
import clsx from 'clsx';

/**
 * Button variants lifted 1-to-1 from agent-system's ui/button.tsx:
 *   default     — solid accent
 *   destructive — solid red
 *   outline     — transparent, bordered
 *   secondary   — overlay background
 *   ghost       — no border, overlay on hover
 *   link        — accent text, underline on hover
 *
 * Sizes: default | sm | lg | icon
 */
export type ButtonVariant =
  | 'default'
  | 'destructive'
  | 'outline'
  | 'secondary'
  | 'ghost'
  | 'link';

export type ButtonSize = 'default' | 'sm' | 'lg' | 'icon';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'default', size = 'default', type = 'button', ...rest }, ref) => (
    <button
      ref={ref}
      type={type}
      className={clsx(
        'ui-btn',
        `ui-btn--${variant}`,
        `ui-btn--size-${size}`,
        className,
      )}
      {...rest}
    />
  ),
);
Button.displayName = 'Button';
